import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useT } from '../i18n/useT';
import DashboardCustomizeRoundedIcon from '@mui/icons-material/DashboardCustomizeRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Link as ScrollLink } from 'react-scroll';

interface Deliverable {
  ns: { title: string; description: string; bullets: string[]; alt: string };
  icon: React.ReactNode;
  image: string;
}

const BuildWithWAI: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });

  const deliverables: Deliverable[] = [
    {
      ns: r.build.apps,
      icon: <DashboardCustomizeRoundedIcon sx={{ fontSize: '1.8rem', color: 'white' }} />,
      image: '/images/generated-app-health-dashboard.png',
    },
    {
      ns: r.build.models,
      icon: <InsightsRoundedIcon sx={{ fontSize: '1.8rem', color: 'white' }} />,
      image: '/images/predictive-model-forecast.png',
    },
    {
      ns: r.build.agents,
      icon: <SmartToyRoundedIcon sx={{ fontSize: '1.8rem', color: 'white' }} />,
      image: '/images/agent-definition.png',
    },
  ];

  return (
    <Box component="section" id="build" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#F8FAFC' }}>
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
        >
          <Typography
            align="center"
            component="span"
            sx={{
              color: '#64748B',
              fontWeight: 700,
              fontSize: '0.8rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              mb: 2,
              display: 'block',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            }}
          >
            {t('build.eyebrow')}
          </Typography>
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '2.8rem' },
              color: '#0F172A',
              letterSpacing: '-0.02em',
            }}
          >
            {t('build.title')}
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{ mb: 7, color: '#64748B', maxWidth: '850px', mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}
          >
            {t('build.subtitle')}
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 5,
          }}
        >
          {deliverables.map((deliv, idx) => (
            <motion.div
              key={deliv.ns.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.15 * idx }}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  background: 'white',
                  border: '1px solid #E2E8F0',
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    borderColor: '#CBD5E1',
                    boxShadow: '0 14px 40px rgba(15, 23, 42, 0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    height: 200,
                    background: '#F8FAFC',
                    overflow: 'hidden',
                    borderBottom: '1px solid #E2E8F0',
                  }}
                >
                  <Box
                    component="img"
                    src={deliv.image}
                    alt={deliv.ns.alt}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'top left',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      width: 44,
                      height: 44,
                      borderRadius: 1.5,
                      backgroundColor: '#0F172A',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 16px rgba(15, 23, 42, 0.25)',
                    }}
                  >
                    {deliv.icon}
                  </Box>
                </Box>
                <Box sx={{ p: 3.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 700, color: '#0F172A', mb: 1, fontSize: '1.3rem', letterSpacing: '-0.01em' }}>
                    {deliv.ns.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6, mb: 2.5, fontSize: '0.95rem' }}>
                    {deliv.ns.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 'auto' }}>
                    {deliv.ns.bullets.map((bullet) => (
                      <Box key={bullet} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <CheckCircleRoundedIcon
                          sx={{ fontSize: '1.1rem', color: '#0F172A', mt: 0.2, flexShrink: 0 }}
                        />
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.9rem', lineHeight: 1.5 }}>
                          {bullet}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <ScrollLink to="deployment" smooth duration={500} offset={-70} style={{ cursor: 'pointer' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 2,
                borderRadius: 2,
                backgroundColor: 'white',
                border: '1px dashed #CBD5E1',
                cursor: 'pointer',
                transition: 'all 0.25s',
                '&:hover': {
                  borderColor: '#0F172A',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography sx={{ color: '#0F172A', fontWeight: 600 }}>
                {t('build.footnote')}
              </Typography>
              <ArrowDownwardIcon sx={{ color: '#0F172A', fontSize: '1.1rem' }} />
            </Box>
          </ScrollLink>
        </motion.div>
      </Container>
    </Box>
  );
};

export default BuildWithWAI;
